import { Button, Flex, HStack, Spacer } from '@chakra-ui/react';
import { type DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import { SearchInput } from '@/components/SearchInput';
import { type CandidateBasicDto } from '@/services/api';

import { type RatingObj } from '../../components/Filters/Rating/RatingFilterComponent';
import {
  CandidatesFilters,
  type StatusItemsObj,
} from '../candidate/CandidatesFilters';
import { useSendToSelectedParticipantsMutation } from './useAdminSurveyQueries';

interface SearchSectionProps {
  searchTerm: string;
  setSearchTerm: (term: string | null) => void;
  onClearSelection: () => void;
  selectedParticipants: Array<CandidateBasicDto>;
  surveyId?: string;
  emailSearchTerm: string;
  setEmailSearchTerm: (value: string | null) => void;
  rangeSearchTerm: Array<number>;
  setRangeSearchTerm: (value: Array<number>) => void;
  status: StatusItemsObj;
  setStatus: (value: StatusItemsObj) => void;
  rating: RatingObj;
  setRating: (value: RatingObj) => void;
  dateRangeSearchTerm: DateRange;
  setDateRangeSearchTerm: (value: DateRange) => void;
  totalParticipants?: number;
  setPage: (page: number) => void;
  clearAllFilters: () => void;
}

export const SurveyParticipantsListSearchSection: React.FC<
  SearchSectionProps
> = ({
  searchTerm,
  setSearchTerm,
  onClearSelection,
  selectedParticipants,
  surveyId,
  emailSearchTerm,
  setEmailSearchTerm,
  rangeSearchTerm,
  setRangeSearchTerm,
  status,
  setStatus,
  rating,
  setRating,
  dateRangeSearchTerm,
  setDateRangeSearchTerm,
  setPage,
  clearAllFilters,
}) => {
  const { t } = useTranslation(['survey', 'components', 'common']);

  const sendToSelectedMutation = useSendToSelectedParticipantsMutation({
    onSuccess: () => {
      onClearSelection();
    },
  });

  const handleSendToSelected = () => {
    if (!surveyId || selectedParticipants.length === 0) return;

    sendToSelectedMutation.mutate({
      surveyId,
      participationIds: selectedParticipants.map(
        (participant) => participant.id
      ),
    });
  };

  return (
    <Flex
      columnGap={2}
      align="center"
      py="12px"
      px="24px"
      justify="space-between"
      borderTopWidth={1}
      borderColor="gray.50"
    >
      <HStack w="40%">
        <CandidatesFilters
          emailSearchTerm={emailSearchTerm}
          setEmailSearchTerm={setEmailSearchTerm}
          rangeSearchTerm={rangeSearchTerm}
          setRangeSearchTerm={setRangeSearchTerm}
          status={status}
          setStatus={setStatus}
          rating={rating}
          setRating={setRating}
          dateRangeSearchTerm={dateRangeSearchTerm}
          setDateRangeSearchTerm={setDateRangeSearchTerm}
          setPage={setPage}
          clearAllFilters={clearAllFilters}
        />

        <SearchInput
          onChange={(value) => {
            setPage(1);
            setSearchTerm(value ?? null);
          }}
          value={searchTerm}
          delay={1000}
        />
      </HStack>
      <Spacer />
      <HStack w="60%" flexWrap="wrap" justifyContent="flex-end">
        <Button
          variant="outline"
          px="3"
          minW="24"
          fontSize="16px"
          isDisabled={
            selectedParticipants.length === 0 ||
            sendToSelectedMutation.isPending
          }
          isLoading={sendToSelectedMutation.isPending}
          onClick={handleSendToSelected}
        >
          {t('survey:sendToSelected', { defaultValue: 'Send to selected' })} (
          {selectedParticipants.length})
        </Button>
      </HStack>
    </Flex>
  );
};
